import React, {
  useState,
  useEffect,
  useTransition,
  Suspense,
  lazy,
} from "react";
import { useTranslation } from "react-i18next";
import i18n from "@core/i18n";
import { useParams, useNavigate } from "react-router-dom";
import {
  FeatureRequest,
  FeatureStatus,
  FeatureRequestWithVote,
} from "@core/types/featureRequests";
import { FeatureRequestService } from "@core/services/FeatureRequestService";
import { useAuth } from "@core/contexts/AuthContext";
import { ErrorBoundary } from "@core/components/ErrorBoundary";
import { FeatureComments } from "./FeatureComments";
import {
  ThumbsUp,
  MessageSquare,
  ExternalLink,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  ArrowLeft,
  Share2,
  Calendar,
  User,
  Pencil,
} from "lucide-react";

// Mode édition pour l'auteur (P2.4) — lazy + ErrorBoundary local côté appelant.
const FeatureRequestForm = lazy(() =>
  import("../../../components/featureRequests/FeatureRequestForm").then(
    (m) => ({
      default: m.FeatureRequestForm,
    }),
  ),
);

const statusConfig = {
  proposed: {
    labelKey: "feature_status_proposed",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    icon: AlertCircle,
  },
  under_review: {
    labelKey: "feature_status_under_review",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    icon: Clock,
  },
  planned: {
    labelKey: "feature_status_planned",
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    icon: TrendingUp,
  },
  in_progress: {
    labelKey: "feature_status_in_progress",
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    icon: AlertCircle,
  },
  completed: {
    labelKey: "feature_status_completed",
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    icon: CheckCircle,
  },
  rejected: {
    labelKey: "feature_status_rejected",
    color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    icon: XCircle,
  },
};

const categoryConfig = {
  export: {
    labelKey: "feature_category_export",
    color: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  integration: {
    labelKey: "feature_category_integration",
    color:
      "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  },
  ui: {
    labelKey: "feature_category_ui",
    color: "bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  },
  mobile: {
    labelKey: "feature_category_mobile",
    color:
      "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  performance: {
    labelKey: "feature_category_performance",
    color:
      "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  },
  security: {
    labelKey: "feature_category_security",
    color: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
  analytics: {
    labelKey: "feature_category_analytics",
    color:
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  },
  collaboration: {
    labelKey: "feature_category_collaboration",
    color: "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  },
  other: {
    labelKey: "feature_category_other",
    color: "bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  },
};

export const FeatureRequestDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isPending, startTransition] = useTransition();

  const [feature, setFeature] = useState<FeatureRequestWithVote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const canAuthorEdit =
    !!feature &&
    !!user &&
    feature.created_by === user.id &&
    (feature.status === "proposed" || feature.status === "under_review");

  const featureRequestService = FeatureRequestService.getInstance();

  useEffect(() => {
    if (id) {
      loadFeatureRequest(id);
    }
  }, [id, i18n.language]);

  const loadFeatureRequest = async (featureId: string) => {
    try {
      setLoading(true);
      setError(null);

      const {
        data,
        success,
        error: serviceError,
      } = await featureRequestService.getFeatureRequest(
        featureId,
        user?.id,
        i18n.language,
      );

      if (!success || !data) {
        throw serviceError || new Error("Failed to load feature request");
      }

      startTransition(() => {
        setFeature(data);
      });
    } catch (err) {
      console.error("Error loading feature request:", err);
      setError(t("feature_not_found"));
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async () => {
    if (!user || !feature) {
      startTransition(() =>
        navigate(
          "/login?redirect=" + encodeURIComponent(window.location.pathname),
        ),
      );
      return;
    }

    try {
      setVoting(true);

      if (feature.user_has_voted) {
        await featureRequestService.removeVote(feature.id, user.id);
        startTransition(() => {
          setFeature((prev) =>
            prev
              ? {
                  ...prev,
                  user_has_voted: false,
                  votes_count: prev.votes_count - 1,
                }
              : null,
          );
        });
      } else {
        await featureRequestService.voteForFeature(feature.id, user.id);
        startTransition(() => {
          setFeature((prev) =>
            prev
              ? {
                  ...prev,
                  user_has_voted: true,
                  votes_count: prev.votes_count + 1,
                }
              : null,
          );
        });
      }
    } catch (err) {
      console.error("Error voting:", err);
      setError(t("errors:vote_failed"));
    } finally {
      setVoting(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: feature?.title,
          text: feature?.description,
          url: url,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        // Show toast or notification
        alert(t("link_copied_clipboard"));
      } catch (err) {
        console.error("Error copying to clipboard:", err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-300">
          {t("loading")}
        </span>
      </div>
    );
  }

  if (error || !feature) {
    return (
      <div className="min-h-screen-safe bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("feature_not_found")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => startTransition(() => navigate("/roadmap"))}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            data-testid="featurerequestdetail-retour-la-roadmap"
          >
            {t("back_to_roadmap")}
          </button>
        </div>
      </div>
    );
  }

  const statusConfigData = statusConfig[feature.status];
  const categoryConfigData = categoryConfig[feature.category || "other"];
  const StatusIcon = statusConfigData.icon;

  return (
    <div className="min-h-screen-safe bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => startTransition(() => navigate("/roadmap"))}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              data-testid="featurerequestdetail-navigate"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("back_to_roadmap")}
            </button>

            <div className="flex items-center gap-2">
              {canAuthorEdit && (
                <button
                  onClick={() => startTransition(() => setShowEditForm(true))}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  data-testid="featurerequestdetail-edit"
                  aria-label={t("edit_my_proposal")}
                >
                  <Pencil className="w-4 h-4" />
                  {t("edit")}
                </button>
              )}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                data-testid="featurerequestdetail-button"
              >
                <Share2 className="w-4 h-4" />
                {t("share")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'édition (P2.4) — boundaries locales */}
      {showEditForm && feature && (
        <ErrorBoundary
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 border border-red-500 rounded-lg p-6 max-w-md w-full text-center">
                <h2 className="text-lg font-semibold mb-2">{t("error")}</h2>
                <button
                  onClick={() => setShowEditForm(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                  data-testid="featurerequestdetail-fermer"
                >
                  {t("close")}
                </button>
              </div>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
              </div>
            }
          >
            <FeatureRequestForm
              existingFeature={feature}
              onSuccess={() => {
                setShowEditForm(false);
                if (feature) loadFeatureRequest(feature.id);
              }}
              onClose={() => startTransition(() => setShowEditForm(false))}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Title Section */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h1>

                {/* Status and Category */}
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfigData.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {t(statusConfigData.labelKey)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${categoryConfigData.color}`}
                  >
                    {t(categoryConfigData.labelKey)}
                  </span>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    {t("proposed_on", {
                      date: new Date(feature.created_at).toLocaleDateString(
                        "fr-FR",
                      ),
                    })}
                  </div>
                  {feature.updated_at !== feature.created_at && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      {t("updated_on", {
                        date: new Date(feature.updated_at).toLocaleDateString(
                          "fr-FR",
                        ),
                      })}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    {feature.created_by ? t("registered_user") : t("anonymous")}
                  </div>
                </div>
              </div>

              {/* Vote Button */}
              <div className="ml-6">
                <button
                  onClick={handleVote}
                  disabled={loading || feature.user_has_voted}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    feature.user_has_voted
                      ? "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                  data-testid="featurerequestdetail-voter"
                >
                  <ThumbsUp className="w-4 h-4" />
                  {feature.user_has_voted ? t("voted") : t("vote")}
                  {feature.votes_count > 0 && (
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                      {feature.votes_count}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t("description")}
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {feature.description}
              </p>
            </div>
          </div>

          {/* Progress Information */}
          {(feature.target_version ||
            feature.estimated_days ||
            feature.admin_notes) && (
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t("progress_information")}
              </h2>
              <div className="space-y-3">
                {feature.target_version && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {t("target_version")}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-sm">
                      {feature.target_version}
                    </span>
                  </div>
                )}

                {feature.estimated_days && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {t("estimate")}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {t("days_count", { count: feature.estimated_days })}
                    </span>
                  </div>
                )}

                {feature.admin_notes && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {t("team_notes")}
                    </span>
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-300">
                        {feature.admin_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GitHub Link */}
          {feature.github_issue_url && (
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t("development_tracking")}
              </h2>
              <a
                href={feature.github_issue_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {t("view_on_github")}
              </a>
            </div>
          )}

          {/* Engagement */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t("engagement")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {feature.votes_count}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t("votes")}
                </div>
              </div>

              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {Math.round((feature.votes_count / 10) * 10)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t("progression")}
                </div>
              </div>

              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {feature.priority ?? 0}
                  <span className="text-base text-gray-500 font-normal">
                    /10
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t("priority")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Commentaires */}
        <FeatureComments featureId={feature.id} />

        {/* Call to Action */}
        {!user && (
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t("sign_in_to_vote")}
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mb-4">
                {t("sign_in_to_vote_description")}
              </p>
              <button
                onClick={() =>
                  startTransition(() =>
                    navigate(
                      "/login?redirect=" +
                        encodeURIComponent(window.location.pathname),
                    ),
                  )
                }
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                data-testid="featurerequestdetail-se-connecter"
              >
                {t("auth:sign_in")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureRequestDetailPage;
